import { type AlertColor } from '@mui/material';
import { type FlexflowEmployeeDataToDisplay } from '../../Contracts/types';
import InfoBanner from 'src/shared/components/InfoBanner';
import i18n from 'src/i18n';
import Grid2 from '@mui/material/Unstable_Grid2';
import moment from 'moment';
import useCountdown from 'src/shared/hooks/useCountdown';
import {
  ContractFlexflowStatusToDisplay,
  type ContractPayflowStatus,
  getStatusToDisplay
} from 'src/modules/Flexflow/Contracts/ContractStatusAfterInvitation';
import type { FlexflowCardStatus } from 'src/modules/Flexflow/types';

const getInfoBannerData = (
  suspendedBySwanAt: string | undefined | null,
  timeLeft: number | null,
  data: FlexflowEmployeeDataToDisplay,
  suspendedByLimit: boolean
): { text: string; severity: AlertColor } | undefined => {
  const status = data?.status as unknown as ContractPayflowStatus;
  const cardStatus = data?.card?.status as unknown as FlexflowCardStatus;
  const statusToDisplay = getStatusToDisplay(status, cardStatus);

  if (suspendedByLimit) {
    return {
      text: i18n.t('card suspended by limit info banner', { ns: 'flexflow' }),
      severity: 'error'
    };
  }

  if (suspendedBySwanAt && timeLeft) {
    return {
      text: i18n.t('card frozen by swan info banner', {
        ns: 'flexflow',
        time: moment.utc(timeLeft).format('HH:mm:ss')
      }),
      severity: 'info'
    };
  }

  if (statusToDisplay === ContractFlexflowStatusToDisplay.SUSPENDED) {
    return {
      text: i18n.t('card terminated info banner', {
        ns: 'flexflow',
        suspendedStatusReason: i18n.t(`card status label.${data.status}`, { ns: 'flexflow' })
      }),
      severity: 'error'
    };
  }

  if (!data.isEligible) {
    const hasCard = Boolean(data.card?.status);

    if (!hasCard) {
      return {
        text: i18n.t('employee not eligible info banner', {
          ns: 'flexflow',
          defaultValue:
            'This employee is not eligible for Flexflow. They must meet the minimum gross salary for Flexible Remuneration or have a Social Benefit balance assigned.'
        }),
        severity: 'error'
      };
    }

    return {
      text: i18n.t('card not eligible info banner', {
        ns: 'flexflow'
      }),
      severity: 'error'
    };
  }
};

const ONE_DAY = 1000 * 60 * 60 * 24;

type SuspendedBannerProps = {
  data: FlexflowEmployeeDataToDisplay;
  suspendedByLimit: boolean;
};

export const SuspendedBanner = ({ data, suspendedByLimit }: SuspendedBannerProps) => {
  const suspendedBySwanAt = data.card?.physicalCard?.suspendedBySwanAt;

  const timeLeft = useCountdown(suspendedBySwanAt ? new Date(new Date(suspendedBySwanAt).getTime() + ONE_DAY) : null);

  if (suspendedBySwanAt && timeLeft && timeLeft <= 0) {
    return <></>;
  }
  const infoBannerData = getInfoBannerData(suspendedBySwanAt, timeLeft, { ...data }, suspendedByLimit);
  if (!infoBannerData) {
    return <></>;
  }
  return (
    <Grid2 mb={4}>
      <InfoBanner {...infoBannerData} />
    </Grid2>
  );
};
